import { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Loader2, Plus, Edit3, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import { RequirementsService, type AtomicRequirement, type UserStory, type Stakeholder } from '../../services/requirementsService';
import { openaiService } from '../../services/openaiService';
import { logger } from '../../services/loggingService';

interface ConversationalRequirementsProps {
  projectId: string;
  userId: string;
}

interface ConversationMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  generatedItems?: {
    requirements?: AtomicRequirement[];
    stories?: UserStory[];
    stakeholders?: Stakeholder[];
  };
}

export function ConversationalRequirements({ projectId, userId }: ConversationalRequirementsProps) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'conversation' | 'requirements' | 'stories' | 'stakeholders'>('conversation');
  const [requirements, setRequirements] = useState<AtomicRequirement[]>([]);
  const [stories, setStories] = useState<UserStory[]>([]);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadExistingData();
    loadInitialPrompt();
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadExistingData = async () => {
    try {
      const [reqs, userStories, stake] = await Promise.all([
        RequirementsService.getAtomicRequirements(projectId),
        RequirementsService.getUserStories(projectId),
        RequirementsService.getStakeholders(projectId)
      ]);
      
      setRequirements(reqs);
      setStories(userStories);
      setStakeholders(stake);
    } catch (error) {
      logger.error('Error loading existing data', { error, projectId });
    }
  };

  const loadInitialPrompt = () => {
    const initialMessage: ConversationMessage = {
      id: 'initial',
      type: 'ai',
      content: `Welcome to conversational requirements gathering! I'm here to help you define your project requirements through natural conversation.

**What would you like to work on?**
- 🎯 **Stakeholders**: "Help me identify stakeholders for my e-commerce app"
- 📝 **User Stories**: "Create user stories for the authentication feature"
- ⚙️ **Requirements**: "Break down the payment system into atomic requirements"
- 🏗️ **Architecture**: "What tech stack should I use for a React app?"
- 🔒 **Security**: "What security requirements do I need for handling payments?"

Just type your question naturally, and I'll help you build a comprehensive project blueprint!`,
      timestamp: new Date()
    };
    setMessages([initialMessage]);
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ConversationMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Process the prompt with AI
      const response = await processPromptWithAI(input.trim());
      
      const aiMessage: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.content,
        timestamp: new Date(),
        generatedItems: response.generatedItems
      };

      setMessages(prev => [...prev, aiMessage]);

      // Update local state with generated items
      if (response.generatedItems?.requirements) {
        setRequirements(prev => [...prev, ...response.generatedItems!.requirements!]);
      }
      if (response.generatedItems?.stories) {
        setStories(prev => [...prev, ...response.generatedItems!.stories!]);
      }
      if (response.generatedItems?.stakeholders) {
        setStakeholders(prev => [...prev, ...response.generatedItems!.stakeholders!]);
      }

    } catch (error) {
      logger.error('Error processing message', { error, projectId });
      const errorMessage: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const processPromptWithAI = async (prompt: string) => {
    // Determine conversation type based on prompt content
    const conversationType = determineConversationType(prompt);
    
    // Create a more detailed prompt for the AI
    const systemPrompt = `You are an expert requirements analyst helping to gather project requirements through conversation. 

Current project context:
- Project ID: ${projectId}
- Existing requirements: ${requirements.length}
- Existing user stories: ${stories.length}
- Existing stakeholders: ${stakeholders.length}

Based on the user's prompt, generate appropriate requirements, user stories, or stakeholders. Be specific and actionable.

Conversation type: ${conversationType}

User prompt: ${prompt}

Respond with:
1. A helpful explanation or analysis
2. Specific, actionable items that can be created
3. Follow-up questions to gather more details

Format your response to be conversational and helpful.`;

    try {
      const aiResponse = await openaiService.generateResponse({
        prompt: systemPrompt,
        userId,
        maxTokens: 2000,
        temperature: 0.7
      });

      // Parse the AI response and extract structured data
      const generatedItems = parseAIResponse(aiResponse, conversationType);

      // Save the conversation
      await RequirementsService.saveConversation(
        projectId,
        conversationType,
        prompt,
        aiResponse,
        Object.values(generatedItems).flat().map(item => item.id || '')
      );

      return {
        content: aiResponse,
        generatedItems
      };
    } catch (error) {
      logger.error('Error calling AI service', { error });
      throw error;
    }
  };

  const determineConversationType = (prompt: string): string => {
    const lowerPrompt = prompt.toLowerCase();
    
    if (lowerPrompt.includes('stakeholder') || lowerPrompt.includes('user') || lowerPrompt.includes('customer')) {
      return 'stakeholder_identification';
    } else if (lowerPrompt.includes('story') || lowerPrompt.includes('user story') || lowerPrompt.includes('as a')) {
      return 'user_stories';
    } else if (lowerPrompt.includes('requirement') || lowerPrompt.includes('atomic') || lowerPrompt.includes('functional')) {
      return 'atomic_requirements';
    } else if (lowerPrompt.includes('architecture') || lowerPrompt.includes('tech stack') || lowerPrompt.includes('technology')) {
      return 'architecture';
    } else if (lowerPrompt.includes('security') || lowerPrompt.includes('secure') || lowerPrompt.includes('protection')) {
      return 'security';
    } else {
      return 'general';
    }
  };

  const parseAIResponse = (response: string, conversationType: string) => {
    // This is a simplified parser - in a real implementation, you'd use more sophisticated NLP
    const generatedItems: any = {};

    // For now, return empty arrays - in a real implementation, you'd parse the AI response
    // and extract structured data to create actual requirements, stories, etc.
    
    return generatedItems;
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessage = (message: ConversationMessage) => (
    <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex gap-3 max-w-3xl ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          message.type === 'user' 
            ? 'bg-accent-500 text-primary-foreground' 
            : 'bg-subtle text-secondary'
        }`}>
          {message.type === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
        </div>
        <div className={`rounded-lg px-4 py-3 ${
          message.type === 'user'
            ? 'bg-accent-500 text-primary-foreground'
            : 'bg-surface border border-default'
        }`}>
          <div className="whitespace-pre-wrap text-sm">{message.content}</div>
          <div className={`text-xs mt-2 ${
            message.type === 'user' ? 'text-primary-foreground/80' : 'text-muted'
          }`}>
            {message.timestamp.toLocaleTimeString()}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequirementsList = () => (
    <div className="space-y-3">
      {requirements.length === 0 ? (
        <div className="text-center py-8 text-text-secondary">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No requirements yet. Start a conversation to generate some!</p>
        </div>
      ) : (
        requirements.map((req) => (
          <div key={req.id} className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-sm text-accent-500">{req.requirement_id}</span>
                  <span className={`badge text-xs ${
                    req.priority === 'critical' ? 'badge-destructive' :
                    req.priority === 'must-have' ? 'badge-warning' :
                    req.priority === 'should-have' ? 'badge-warning' :
                    'badge-secondary'
                  }`}>
                    {req.priority}
                  </span>
                  <span className="badge badge-primary text-xs">
                    {req.type}
                  </span>
                </div>
                <p className="text-text mb-2">{req.description}</p>
                <div className="text-sm text-text-secondary">
                  <span className="font-medium">System:</span> {req.system} | 
                  <span className="font-medium ml-1">Category:</span> {req.category}
                </div>
              </div>
              <button className="p-1 hover:bg-background-secondary rounded">
                <Edit3 className="w-4 h-4 text-text-tertiary" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderStoriesList = () => (
    <div className="space-y-3">
      {stories.length === 0 ? (
        <div className="text-center py-8 text-text-secondary">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No user stories yet. Start a conversation to generate some!</p>
        </div>
      ) : (
        stories.map((story) => (
          <div key={story.id} className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-sm text-success">{story.story_id}</span>
                  <span className={`badge text-xs ${
                    story.priority === 'critical' ? 'badge-destructive' :
                    story.priority === 'must-have' ? 'badge-warning' :
                    story.priority === 'should-have' ? 'badge-warning' :
                    'badge-secondary'
                  }`}>
                    {story.priority}
                  </span>
                  <span className="badge badge-secondary text-xs">
                    {story.story_points} pts
                  </span>
                </div>
                <h4 className="font-medium text-text mb-1">{story.title}</h4>
                <p className="text-text-secondary text-sm mb-2">{story.description}</p>
                <div className="text-sm text-text-secondary">
                  <span className="font-medium">As a</span> {story.user_type}, <span className="font-medium">I want</span> {story.feature}, <span className="font-medium">so that</span> {story.benefit}
                </div>
              </div>
              <button className="p-1 hover:bg-background-secondary rounded">
                <Edit3 className="w-4 h-4 text-text-tertiary" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderStakeholdersList = () => (
    <div className="space-y-3">
      {stakeholders.length === 0 ? (
        <div className="text-center py-8 text-text-secondary">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No stakeholders yet. Start a conversation to generate some!</p>
        </div>
      ) : (
        stakeholders.map((stakeholder) => (
          <div key={stakeholder.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">{stakeholder.name}</h4>
                  <span className="badge badge-primary text-xs">
                    {stakeholder.role}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    stakeholder.influence_level === 'high' ? 'badge-destructive' :
                    stakeholder.influence_level === 'medium' ? 'badge-warning' :
                    'badge-secondary'
                  }`}>
                    {stakeholder.influence_level} influence
                  </span>
                </div>
                {stakeholder.description && (
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{stakeholder.description}</p>
                )}
                {stakeholder.goals.length > 0 && (
                  <div className="mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Goals:</span>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 ml-4 list-disc">
                      {stakeholder.goals.map((goal, index) => (
                        <li key={index}>{goal}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <Edit3 className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Conversational Requirements
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Define your project requirements through natural conversation with AI
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-1 p-4">
          {[
            { id: 'conversation', label: 'Conversation', count: messages.length },
            { id: 'requirements', label: 'Requirements', count: requirements.length },
            { id: 'stories', label: 'User Stories', count: stories.length },
            { id: 'stakeholders', label: 'Stakeholders', count: stakeholders.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-accent-100 text-accent-700 dark:bg-accent-900 dark:text-accent-200'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-xs rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'conversation' ? (
          <div className="h-full flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map(renderMessage)}
              {isLoading && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-accent-600" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything about your project requirements..."
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    rows={3}
                    disabled={isLoading}
                  />
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading}
                  className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : activeTab === 'requirements' ? (
          <div className="h-full overflow-y-auto p-4">
            {renderRequirementsList()}
          </div>
        ) : activeTab === 'stories' ? (
          <div className="h-full overflow-y-auto p-4">
            {renderStoriesList()}
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-4">
            {renderStakeholdersList()}
          </div>
        )}
      </div>
    </div>
  );
}
